import * as Astronomy from "astronomy-engine";
import type {
  BodyPosition,
  CatalogStar,
  HorizontalPosition,
  ObserverLocation,
  SkyCatalog,
  StarPosition,
  TimelineMarker,
} from "./types";

const RadToDeg = 180 / Math.PI;
const RadToHour = 12 / Math.PI;

const bodyDefinitions = [
  { id: "Moon", body: Astronomy.Body.Moon, label: "Moon", kind: "Moon", conceptIds: ["altaz", "rise_set"] },
  { id: "Mercury", body: Astronomy.Body.Mercury, label: "Mercury", kind: "Planet", conceptIds: ["altaz", "rise_set"] },
  { id: "Venus", body: Astronomy.Body.Venus, label: "Venus", kind: "Planet", conceptIds: ["altaz", "rise_set"] },
  { id: "Mars", body: Astronomy.Body.Mars, label: "Mars", kind: "Planet", conceptIds: ["altaz", "rise_set"] },
  { id: "Jupiter", body: Astronomy.Body.Jupiter, label: "Jupiter", kind: "Planet", conceptIds: ["altaz", "rise_set"] },
  { id: "Saturn", body: Astronomy.Body.Saturn, label: "Saturn", kind: "Planet", conceptIds: ["altaz", "rise_set"] },
  { id: "Uranus", body: Astronomy.Body.Uranus, label: "Uranus", kind: "Planet", conceptIds: ["altaz", "rise_set"] },
  { id: "Neptune", body: Astronomy.Body.Neptune, label: "Neptune", kind: "Planet", conceptIds: ["altaz", "rise_set"] },
];

export function starHorizontal(
  star: CatalogStar,
  observer: ObserverLocation,
  date: Date,
): HorizontalPosition {
  const result = Astronomy.Horizon(
    date,
    toObserver(observer),
    star.raRadJ2000 * RadToHour,
    star.decRadJ2000 * RadToDeg,
  );
  return {
    altitudeDeg: result.altitude,
    azimuthDeg: normalizeDegrees(result.azimuth),
  };
}

export function starPositions(
  catalog: SkyCatalog,
  observer: ObserverLocation,
  date: Date,
): StarPosition[] {
  return catalog.stars.map((star) => {
    const horizontal = starHorizontal(star, observer, date);
    return {
      ...horizontal,
      id: `HR${star.hr}`,
      label: star.properName,
      kind: "star",
      hr: star.hr,
      star,
      magnitude: star.visualMagnitude,
      fact: star.fact,
      subtitle: `HR ${star.hr} | mag ${formatMagnitude(star.visualMagnitude)} | ${directionLabel(horizontal)}`,
      conceptIds: ["altaz", "magnitude", "rise_set", "transit"],
    };
  });
}

export function bodyPositions(observer: ObserverLocation, date: Date): BodyPosition[] {
  const astronomyObserver = toObserver(observer);
  return bodyDefinitions.map((definition) => {
    const equatorial = Astronomy.Equator(definition.body, date, astronomyObserver, true, true);
    const horizontal = Astronomy.Horizon(date, astronomyObserver, equatorial.ra, equatorial.dec, "normal");
    const magnitude = Astronomy.Illumination(definition.body, date).mag;
    const position = {
      altitudeDeg: horizontal.altitude,
      azimuthDeg: normalizeDegrees(horizontal.azimuth),
    };
    return {
      ...position,
      id: definition.id,
      bodyId: definition.id,
      label: definition.label,
      kind: "body",
      magnitude,
      subtitle: `${definition.kind} | ${directionLabel(position)}`,
      fact: bodyFact(definition.id),
      conceptIds: definition.conceptIds,
    };
  });
}

export function riseSetSummaryForStar(
  star: CatalogStar,
  observer: ObserverLocation,
  date: Date,
): { rise?: Date; set?: Date; transit?: Date } {
  const astronomyObserver = toObserver(observer);
  const start = startOfLocalDay(date);
  Astronomy.DefineStar(
    Astronomy.Body.Star1,
    star.raRadJ2000 * RadToHour,
    star.decRadJ2000 * RadToDeg,
    1000,
  );
  return {
    rise: Astronomy.SearchRiseSet(Astronomy.Body.Star1, astronomyObserver, 1, start, 1)?.date,
    set: Astronomy.SearchRiseSet(Astronomy.Body.Star1, astronomyObserver, -1, start, 1)?.date,
    transit: Astronomy.SearchHourAngle(Astronomy.Body.Star1, astronomyObserver, 0, start, 1)?.time.date,
  };
}

export function timelineMarkers(date: Date): TimelineMarker[] {
  const start = startOfLocalDay(date).getTime();
  const hours = [18, 20, 22, 24, 26, 28, 30];
  return hours.map((hour) => ({
    label: hour < 24 ? `${hour}:00` : `${hour - 24}:00`,
    epochMillis: start + hour * 60 * 60 * 1000,
  }));
}

export function directionLabel(horizontal: HorizontalPosition): string {
  return `${cardinalDirection(horizontal.azimuthDeg)} | ${Math.round(horizontal.altitudeDeg)} deg`;
}

export function visibilityPhrase(altitudeDeg: number): string {
  if (altitudeDeg >= 60) return "high overhead";
  if (altitudeDeg >= 25) return "well placed";
  if (altitudeDeg >= 5) return "low but visible";
  if (altitudeDeg >= 0) return "on the horizon";
  return "below the horizon";
}

export function formatMagnitude(value: number): string {
  return value.toFixed(1);
}

export function normalizeDegrees(value: number): number {
  return ((value % 360) + 360) % 360;
}

export function toObserver(observer: ObserverLocation): Astronomy.Observer {
  return new Astronomy.Observer(observer.lat, observer.lon, 0);
}

function cardinalDirection(azimuthDeg: number): string {
  const labels = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return labels[Math.floor((normalizeDegrees(azimuthDeg) + 22.5) / 45) % labels.length] ?? "N";
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function bodyFact(id: string): string {
  switch (id) {
    case "Moon":
      return "The Moon changes position quickly from night to night, making it the easiest moving body to track.";
    case "Venus":
      return "Venus is often the brightest planet, visible near sunset or sunrise when its geometry lines up.";
    case "Jupiter":
      return "Jupiter is bright enough to stand out even from city skies and often anchors the planet line.";
    case "Saturn":
      return "Saturn is fainter than Jupiter but still distinct when it is high and steady.";
    case "Uranus":
      return "Uranus is barely visible to dark-adapted eyes from very dark skies, so it is usually a binocular or telescope target.";
    case "Neptune":
      return "Neptune is too faint for naked-eye observing, but planning its position helps with binocular or telescope sessions.";
    case "Mars":
      return "Mars becomes strikingly bright near opposition and softer when it is farther from Earth.";
    default:
      return `${id} stays close to the ecliptic, the broad path followed by solar-system bodies.`;
  }
}
